const BACKEND = import.meta.env.VITE_API_URL ?? ''

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export async function waitForBackend(
  timeoutMs = 150_000,
  intervalMs = 2_500,
): Promise<boolean> {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    try {
      const r = await fetch(`${BACKEND}/health`, {
        cache: 'no-store',
        signal: AbortSignal.timeout(5_000),
      })
      if (r.ok) return true
    } catch {
      // expected during cold start
    }
    await sleep(intervalMs)
  }
  return false
}

/**
 * Whether the database behind a live backend is answering.
 *
 * `/health` only proves the process is up — with the Supabase project paused it
 * still answers, and the app would load into a backend whose every call fails.
 * Only a 503 DATABASE_UNAVAILABLE (or no answer at all) counts as unavailable;
 * any other response is not a database signal, so it never blocks the app.
 */
export async function checkDatabaseReady(
  attempts = 4,
  intervalMs = 2_500,
): Promise<'ready' | 'unavailable'> {
  for (let i = 0; i < attempts; i++) {
    if (i > 0) await sleep(intervalMs)
    try {
      const r = await fetch(`${BACKEND}/ready`, {
        cache: 'no-store',
        signal: AbortSignal.timeout(8_000),
      })
      if (r.status !== 503) return 'ready'
      const body: unknown = await r.json().catch(() => null)
      const code = (body as { error?: { code?: unknown } } | null)?.error?.code
      if (code !== 'DATABASE_UNAVAILABLE') return 'ready'
    } catch {
      // no answer — keep trying, then treat as unavailable
    }
  }
  return 'unavailable'
}
